import { type FC, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  Dialog,
  DialogContent,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  ListSubheader,
  Skeleton,
  Typography,
  type SvgIconProps,
} from "@mui/material";
import NoteAddOutlinedIcon from "@mui/icons-material/NoteAddOutlined";
import ShoppingBagOutlinedIcon from "@mui/icons-material/ShoppingBagOutlined";
import LiveTvOutlinedIcon from "@mui/icons-material/LiveTvOutlined";
import OndemandVideoOutlinedIcon from "@mui/icons-material/OndemandVideoOutlined";
import ArticleOutlinedIcon from "@mui/icons-material/ArticleOutlined";
import AutoAwesomeOutlinedIcon from "@mui/icons-material/AutoAwesomeOutlined";
import AccountTreeOutlinedIcon from "@mui/icons-material/AccountTreeOutlined";
import PersonAddAltOutlinedIcon from "@mui/icons-material/PersonAddAltOutlined";
import PlayCircleOutlineIcon from "@mui/icons-material/PlayCircleOutline";
import RemoveShoppingCartOutlinedIcon from "@mui/icons-material/RemoveShoppingCartOutlined";
import EventBusyOutlinedIcon from "@mui/icons-material/EventBusyOutlined";
import SyncDisabledOutlinedIcon from "@mui/icons-material/SyncDisabledOutlined";
import ReplayOutlinedIcon from "@mui/icons-material/ReplayOutlined";
import MoneyOffOutlinedIcon from "@mui/icons-material/MoneyOffOutlined";
import { useSnackbar } from "notistack";
import { useTranslation } from "react-i18next";
import {
  AutomationKindType,
  AutomationTriggerType,
} from "@/types/graphql/partner.generated";
import {
  marketingErrorKey,
  useAutomationGraphRulesQuery,
  useCreateAutomationMutation,
  type AutomationTemplateItem,
} from "@/partner/entities/marketing";
import type { MutationErrorLike } from "@/partner/shared/lib";
import { useDeviceBreakpoint } from "@/partner/shared/hooks";
import { DialogHeader } from "@/shared/components";
import {
  BLANK_JOURNEY_TEMPLATE_LABELS,
  BLANK_TEMPLATE_LABELS,
  TEMPLATE_HINT_KEYS,
  isPlatformTrigger,
  templateLabels,
  type AutomationTemplateLabels,
  type PlatformTrigger,
  triggerKey,
} from "../model/automationLabels";
import {
  TEMPLATE_GROUPS,
  TEMPLATE_GROUP_KEYS,
  journeyTemplateGroup,
  type TemplateGroup,
} from "../model/templateGroups";
import {
  createAutomationInput,
  type CreateKind,
} from "../model/createAutomationInput";

/** Иконка шаблона по его событию; событие, которого кабинет не знает, даёт общую. */
const TRIGGER_ICONS: Record<PlatformTrigger, FC<SvgIconProps>> = {
  [AutomationTriggerType.ProductPublishedType]: ShoppingBagOutlinedIcon,
  [AutomationTriggerType.StreamAnnouncedType]: LiveTvOutlinedIcon,
  [AutomationTriggerType.ContentPublishedType]: OndemandVideoOutlinedIcon,
  [AutomationTriggerType.PostPublishedType]: ArticleOutlinedIcon,
};

/** Иконка шаблона цепочки по событию его триггера (вне карты: общая). */
const JOURNEY_TRIGGER_ICONS: Partial<
  Record<AutomationTriggerType, FC<SvgIconProps>>
> = {
  [AutomationTriggerType.UserRegisteredType]: PersonAddAltOutlinedIcon,
  [AutomationTriggerType.ProductPurchasedType]: ShoppingBagOutlinedIcon,
  [AutomationTriggerType.ViewStartedType]: PlayCircleOutlineIcon,
  [AutomationTriggerType.CheckoutStartedType]: RemoveShoppingCartOutlinedIcon,
  [AutomationTriggerType.SubscriptionEndingType]: EventBusyOutlinedIcon,
  [AutomationTriggerType.SubscriptionCancelledType]: SyncDisabledOutlinedIcon,
  [AutomationTriggerType.SubscriptionExpiredType]: ReplayOutlinedIcon,
  [AutomationTriggerType.RenewalFailedType]: MoneyOffOutlinedIcon,
};

const templateIcon = (
  kind: AutomationKindType,
  trigger: AutomationTemplateItem["trigger"]
): FC<SvgIconProps> => {
  if (kind === AutomationKindType.JourneyType) {
    return (
      (trigger ? JOURNEY_TRIGGER_ICONS[trigger] : undefined) ??
      AutoAwesomeOutlinedIcon
    );
  }
  return (
    (isPlatformTrigger(trigger) ? TRIGGER_ICONS[trigger] : undefined) ??
    AutoAwesomeOutlinedIcon
  );
};

interface Choice {
  kind: CreateKind;
  /** Ключ шаблона бэкенда; null у пустой автоматизации или цепочки. */
  templateKey: string | null;
  labels: AutomationTemplateLabels;
  Icon: FC<SvgIconProps>;
  /** Подсказка о пересечении с другими цепочками; у остальных null. */
  hintKey: string | null;
  /** Подпись события шаблона цепочки (отличает похожие шаблоны); иначе null. */
  triggerLabelKey: string | null;
}

const BLANK_CHOICES: Record<CreateKind, Choice> = {
  [AutomationKindType.PlatformEventType]: {
    kind: AutomationKindType.PlatformEventType,
    templateKey: null,
    labels: BLANK_TEMPLATE_LABELS,
    Icon: NoteAddOutlinedIcon,
    hintKey: null,
    triggerLabelKey: null,
  },
  [AutomationKindType.JourneyType]: {
    kind: AutomationKindType.JourneyType,
    templateKey: null,
    labels: BLANK_JOURNEY_TEMPLATE_LABELS,
    Icon: AccountTreeOutlinedIcon,
    hintKey: null,
    triggerLabelKey: null,
  },
};

// Ре-ревью I1: пока шаблоны ещё не пришли (холодный кеш, страница
// открыта прямо на ?tab=create), под пустым вариантом раздела рисуем
// строки-скелетоны, чтобы диалог не подрастал по высоте, когда ответ
// придёт. Число не завязано на бизнес-правило: это заглушка макета на
// раздел, ошибка в счёте даст лишь небольшой остаточный скачок.
const TEMPLATE_SKELETON_ROWS = 4;

interface CreateAutomationDialogProps {
  onClose: () => void;
  /** Автоматизация создана: список переключает адрес на ?tab=edit&id=. */
  onCreated: (id: string) => void;
}

/** Ключ строки: пустые варианты двух видов не совпадают. */
const choiceId = (choice: Choice) =>
  `${choice.kind}:${choice.templateKey ?? ""}`;

type TemplatesQuery = ReturnType<typeof useAutomationGraphRulesQuery>;

/** Строки шаблонов одного вида в порядке сервера. */
const templateChoices = (
  kind: CreateKind,
  templates: AutomationTemplateItem[]
): { choice: Choice; trigger: AutomationTemplateItem["trigger"] }[] => {
  const journey = kind === AutomationKindType.JourneyType;
  return templates.map((template) => ({
    trigger: template.trigger,
    choice: {
      kind,
      templateKey: template.key,
      labels: templateLabels(template.key),
      Icon: templateIcon(kind, template.trigger),
      hintKey: journey ? (TEMPLATE_HINT_KEYS.get(template.key) ?? null) : null,
      triggerLabelKey:
        journey && template.trigger ? triggerKey(template.trigger) : null,
    },
  }));
};

/** Состояние запроса шаблонов: строки-скелетоны или ошибка с «Повторить». */
interface QueryStatus {
  query: TemplatesQuery;
  loading: boolean;
  failed: boolean;
}

const queryStatus = (query: TemplatesQuery): QueryStatus => ({
  query,
  loading: query.loading && !query.data,
  failed: !query.data && Boolean(query.error),
});

interface Section {
  group: TemplateGroup;
  choices: Choice[];
  /** Загрузка и сбой шаблонов событий платформы живут в своей группе. */
  status: QueryStatus | null;
}

/**
 * Выбор начала (?tab=create): один список с группами. «С нуля» (пустая
 * автоматизация и пустая цепочка), «События платформы» (шаблоны
 * automationGraphRules(kind: PLATFORM_EVENT)), затем шаблоны цепочек по
 * семье события их триггера (templateGroups.ts): новые зрители, покупки и
 * просмотры, подписки, неизвестное событие в «Другое». Список, порядок и
 * триггер шаблонов отдаёт сервер, кабинет держит только тексты, иконки,
 * подсказки и карту групп. Пустые группы не рисуются.
 *
 * Ре-ревью I1: список страницы (Automations.tsx) греет оба запроса
 * cache-first заранее, поэтому обычно диалог открывается сразу с
 * шаблонами. На холодном кеше шаблоны каждого вида, пока не пришли,
 * заменены строками со Skeleton той же высоты (событий платформы в своей
 * группе, цепочек в конце списка, где встанут их группы). При сбое список
 * говорит об этом и даёт «Повторить».
 */
export const CreateAutomationDialog: FC<CreateAutomationDialogProps> = ({
  onClose,
  onCreated,
}) => {
  const { t } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();
  const isXs = useDeviceBreakpoint("sm");
  const [pending, setPending] = useState<string | null>(null);
  const eventTemplatesQuery = useAutomationGraphRulesQuery({
    variables: { kind: AutomationKindType.PlatformEventType },
  });
  const journeyTemplatesQuery = useAutomationGraphRulesQuery({
    variables: { kind: AutomationKindType.JourneyType },
  });
  const [createAutomation, { loading }] = useCreateAutomationMutation({
    onError: (error: MutationErrorLike) =>
      enqueueSnackbar(t(marketingErrorKey(error)), { variant: "error" }),
  });

  const onPick = async (choice: Choice) => {
    setPending(choiceId(choice));
    const result = await createAutomation({
      variables: { input: createAutomationInput(choice, t) },
    });
    setPending(null);
    const id = result.data?.createAutomation.id;
    if (id) {
      enqueueSnackbar(t("automations.create.success"), { variant: "success" });
      onCreated(id);
    }
  };

  const eventStatus = queryStatus(eventTemplatesQuery);
  const journeyStatus = queryStatus(journeyTemplatesQuery);
  const eventChoices = templateChoices(
    AutomationKindType.PlatformEventType,
    eventTemplatesQuery.data?.automationGraphRules.templates ?? []
  ).map(({ choice }) => choice);
  const journeyChoices = templateChoices(
    AutomationKindType.JourneyType,
    journeyTemplatesQuery.data?.automationGraphRules.templates ?? []
  );

  const sections: Section[] = TEMPLATE_GROUPS.map((group): Section => {
    if (group === "scratch") {
      return {
        group,
        choices: [
          BLANK_CHOICES[AutomationKindType.PlatformEventType],
          BLANK_CHOICES[AutomationKindType.JourneyType],
        ],
        status: null,
      };
    }
    if (group === "platformEvents") {
      return { group, choices: eventChoices, status: eventStatus };
    }
    return {
      group,
      choices: journeyChoices
        .filter(({ trigger }) => journeyTemplateGroup(trigger) === group)
        .map(({ choice }) => choice),
      status: null,
    };
  }).filter(
    ({ choices, status }) =>
      choices.length > 0 || Boolean(status?.loading || status?.failed)
  );

  const triggerChip = (labelKey: string) => (
    <Chip
      size="small"
      variant="outlined"
      label={t(labelKey)}
      sx={{ flexShrink: 0, maxWidth: "100%" }}
    />
  );

  const renderChoice = (choice: Choice) => (
    <ListItemButton
      key={choiceId(choice)}
      disabled={loading}
      selected={pending === choiceId(choice)}
      onClick={() => onPick(choice)}
      sx={{ gap: 2, alignItems: "flex-start" }}
    >
      <ListItemIcon sx={{ minWidth: 0, mt: 0.5 }}>
        <choice.Icon />
      </ListItemIcon>
      <ListItemText
        sx={{ minWidth: 0, my: 0.5 }}
        primary={
          <>
            {t(choice.labels.titleKey)}
            {/* На узком экране событие под заголовком, на широком справа. */}
            {isXs && choice.triggerLabelKey && (
              <Box component="span" sx={{ display: "block", mt: 0.5 }}>
                {triggerChip(choice.triggerLabelKey)}
              </Box>
            )}
          </>
        }
        secondary={
          <>
            {/* Описание в две строки: одной не хватает русским текстам. */}
            <Typography
              component="span"
              variant="body2"
              color="text.secondary"
              sx={{
                display: "-webkit-box",
                WebkitLineClamp: 2,
                WebkitBoxOrient: "vertical",
                overflow: "hidden",
              }}
            >
              {t(choice.labels.descriptionKey)}
            </Typography>
            {choice.hintKey && (
              <Typography
                component="span"
                variant="caption"
                color="text.secondary"
                sx={{ display: "block", mt: 0.5 }}
              >
                {t(choice.hintKey)}
              </Typography>
            )}
          </>
        }
      />
      {!isXs && choice.triggerLabelKey && (
        <Box sx={{ mt: 0.5, flexShrink: 0, maxWidth: "40%" }}>
          {triggerChip(choice.triggerLabelKey)}
        </Box>
      )}
    </ListItemButton>
  );

  const renderSkeletons = () =>
    Array.from({ length: TEMPLATE_SKELETON_ROWS }, (_, index) => (
      <ListItemButton key={`skeleton-${index}`} disabled>
        <ListItemIcon>
          <Skeleton variant="circular" width={24} height={24} />
        </ListItemIcon>
        <ListItemText
          primary={<Skeleton variant="text" width="40%" />}
          secondary={<Skeleton variant="text" width="70%" />}
        />
      </ListItemButton>
    ));

  const renderFailure = ({ query }: QueryStatus) => (
    <Alert
      severity="error"
      sx={{ m: 1 }}
      action={
        <Button
          color="inherit"
          size="small"
          disabled={query.loading}
          onClick={() => void query.refetch().catch(() => undefined)}
        >
          {t("automations.list.retry")}
        </Button>
      }
    >
      {t("automations.create.loadFailed")}
    </Alert>
  );

  const renderStatus = (status: QueryStatus | null) => (
    <>
      {status?.loading && renderSkeletons()}
      {status?.failed && renderFailure(status)}
    </>
  );

  return (
    <Dialog
      open
      onClose={loading ? undefined : onClose}
      fullWidth
      maxWidth="sm"
      scroll="paper"
      fullScreen={isXs}
    >
      <DialogHeader
        title={t("automations.create.title")}
        loading={loading}
        onClose={onClose}
      />
      <DialogContent sx={{ p: 0 }}>
        <List disablePadding sx={{ pb: 1 }}>
          {sections.map(({ group, choices, status }) => (
            <li key={group}>
              <List disablePadding>
                {/* Липкий заголовок на фоне бумаги диалога: тема красит
                    MuiDialog paper в background.default, другой фон дал бы
                    полосу под заголовком. */}
                <ListSubheader sx={{ bgcolor: "background.default" }}>
                  {t(TEMPLATE_GROUP_KEYS[group])}
                </ListSubheader>
                {choices.map(renderChoice)}
                {renderStatus(status)}
              </List>
            </li>
          ))}
          {(journeyStatus.loading || journeyStatus.failed) && (
            <li>
              <List disablePadding>{renderStatus(journeyStatus)}</List>
            </li>
          )}
        </List>
      </DialogContent>
    </Dialog>
  );
};
